
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PlatformStates } from "../types";
import { toast } from "sonner";
import { databaseMonitor } from "@/utils/performance/databaseMonitoring";

export const useCredentialsSaver = (
  platformStates: PlatformStates,
  setPlatformStates: React.Dispatch<React.SetStateAction<PlatformStates>>
) => {
  const [isSubmitting, setIsSubmitting] = useState<Record<string, boolean>>({});

  const handleSaveCredentials = async (platformId: string) => {
    try {
      setIsSubmitting(prev => ({ ...prev, [platformId]: true }));
      console.log('Saving credentials for platform:', platformId);
      
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No authenticated user found');
        toast.error("Please log in to save credentials");
        return;
      }
      
      const platformState = platformStates[platformId];
      if (!platformState || !platformState.credentials) {
        console.error('No credentials found for platform:', platformId);
        toast.error("No credentials to save");
        return;
      }
      
      // Check if credentials contain actual data (not just masked values)
      const hasRealCredentials = Object.values(platformState.credentials).some(
        value => value && value !== '••••••••••••••••'
      );
      
      if (!hasRealCredentials) {
        console.log('No new credentials to save for platform:', platformId);
        toast.info("No changes to save");
        return;
      }
      
      console.log('Preparing to save credentials with user_id:', user.id);
      
      // Filter out masked credentials before saving
      const credentialsToSave = Object.fromEntries(
        Object.entries(platformState.credentials).filter(
          ([_, value]) => value && value !== '••••••••••••••••'
        )
      );
      
      console.log('Credentials to save:', Object.keys(credentialsToSave));
      
      // Always set new credentials as active for the current user
      const { error } = await supabase
        .from('platform_credentials')
        .upsert({
          user_id: user.id,
          platform: platformId,
          credentials: credentialsToSave,
          is_active: true, // Automatically activate new credentials
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,platform'
        });
      
      if (error) {
        console.error('Database error saving credentials:', error);
        databaseMonitor.logError(error.message, {
          table: 'platform_credentials',
          context: 'UPSERT credentials'
        });
        
        // Provide more specific error messages
        if (error.message.includes('unique constraint')) {
          toast.error("Credential saving conflict. Please try again.");
        } else if (error.message.includes('permission')) {
          toast.error("Permission denied. Please check your access rights.");
        } else {
          toast.error(`Failed to save credentials: ${error.message}`);
        }
        
        // Update state to reflect error
        setPlatformStates(prev => ({
          ...prev,
          [platformId]: {
            ...prev[platformId],
            status: 'error'
          }
        }));
        return;
      }
      
      console.log('Successfully saved and activated credentials for platform:', platformId);
      
      // Update state to show saved status with masked credentials
      const maskedCredentials = Object.fromEntries(
        Object.keys(credentialsToSave).map(key => [key, '••••••••••••••••'])
      );
      
      setPlatformStates(prev => ({
        ...prev,
        [platformId]: {
          ...prev[platformId],
          credentials: maskedCredentials,
          status: 'valid',
          enabled: true // Also enable the platform when credentials are saved
        }
      }));
      
      toast.success(`${platformId} credentials saved and activated successfully!`);
      
    } catch (error) {
      console.error('Error saving credentials:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      databaseMonitor.logError(errorMessage, {
        table: 'platform_credentials',
        context: 'save_credentials_handler'
      });
      
      toast.error(`Failed to save credentials: ${errorMessage}`);
      
      setPlatformStates(prev => ({
        ...prev,
        [platformId]: {
          ...prev[platformId],
          status: 'error'
        }
      }));
      
    } finally {
      setIsSubmitting(prev => ({ ...prev, [platformId]: false }));
    }
  };

  return {
    isSubmitting,
    handleSaveCredentials
  };
};
